import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Phone, MapPin, Globe } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = i18n.language || 'es';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsLangMenuOpen(false);
  };

  const goToHome = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setIsMobileMenuOpen(false);
  };

  const menuItems = [
    {
      label: t('nav.facialSurgery'),
      href: '/cirugia-facial',
      items: [
        { label: t('nav.blepharoplasty'), href: '/blefaroplastia' },
        { label: t('nav.facelift'), href: '/lifting-facial' },
        { label: t('nav.rhinoplasty'), href: '/rinoplastia' },
        { label: t('nav.otoplasty'), href: '/otoplastia' },
      ]
    },
    {
      label: t('nav.bodySurgery'),
      href: '/cirugia-corporal',
      items: [
        { label: t('nav.breastSurgery'), href: '/cirugia-mamas' },
        { label: t('nav.gynecomastia'), href: '/ginecomastia' },
        { label: t('nav.abdominoplasty'), href: '/abdominoplastia' },
        { label: t('nav.postBariatric'), href: '/cirugia-postbariatrica' },
        { label: t('nav.liposuction'), href: '/liposuccion' },
        { label: t('nav.brachioplasty'), href: '/braquioplastia' },
        { label: t('nav.gynecologicalAesthetics'), href: '/gineco-estetica' },
      ]
    },
    {
      label: t('nav.aestheticMedicine'),
      href: '/medicina-estetica',
      items: [
        { label: t('nav.rhinomodeling'), href: '/rinomodelacion' },
        { label: t('nav.endolifting'), href: '/endolifting' },
        { label: t('nav.jowlRemoval'), href: '/papada' },
        { label: t('nav.browLift'), href: '/elevacion-cejas' },
        { label: t('nav.facialHarmonization'), href: '/armonizacion-facial' },
        { label: t('nav.darkCircles'), href: '/ojeras-sin-cirugia' },
        { label: t('nav.integrativeMedicine'), href: '/medicina-integrativa' },
        { label: t('nav.gynecologicalAesthetics'), href: '/gineco-estetica' },
      ]
    },
    {
      label: t('nav.reconstructiveSurgery'),
      href: '/cirugia-reparadora',
      items: [
        { label: t('nav.dermatologicalSurgery'), href: '/cirugia-dermatologica' },
        { label: t('nav.secondaryPlasticSurgery'), href: '/cirugias-secundarias' },
        { label: t('nav.secondOpinion'), href: '/segunda-opinion' },
        { label: t('nav.medicalExpertise'), href: '/peritacion-medica' },
      ]
    },
    {
      label: t('nav.theDoctor'),
      href: '/el-doctor',
      items: [
        { label: t('nav.biography'), href: '/el-doctor#biografia' },
        { label: t('nav.philosophy'), href: '/el-doctor#filosofia' },
        { label: t('nav.trademarks'), href: '/el-doctor#marcas' },
        { label: 'Metasigna', href: '/el-doctor#metasigna' },
        { label: t('nav.clinic'), href: '/el-doctor#clinica' },
        // { label: t('nav.certifications'), href: '/el-doctor/acreditaciones' },
        { divider: true },
        { label: t('nav.professionalsTitle'), href: null, heading: true },
        { label: t('nav.partnerClinicsAccess'), href: '/clinica/login' },
        { label: t('nav.guidesProtocols'), href: '/guias-recursos' },
      ]
    },
    {
      label: t('nav.recoveryCenter'),
      href: '/recuperacion',
      items: [
        { label: t('nav.preparation'), href: '/preparacion' },
        { label: t('nav.recovery'), href: '/recuperacion' },
        { label: 'Skin Health', href: '/skin-health' },
      ]
    },
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'header-blur border-b border-divider' 
          : 'bg-ivory'
      }`}>
        {/* Top bar */}
        <div className="hidden lg:block bg-charcoal text-gold-light">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between h-10 text-xs tracking-wider">
              {/* Language Selector */}
              <div className="relative flex items-center gap-2">
                <Globe size={12} className="text-gold" />
                <div className="flex items-center gap-3">
                  {Object.entries(languages).map(([code, lang], index) => (
                    <React.Fragment key={code}>
                      {index > 0 && <span className="text-gold-light/30">|</span>}
                      <button
                        onClick={() => changeLanguage(code)}
                        className={`transition-colors hover:text-gold ${
                          currentLang === code 
                            ? 'text-gold font-medium' 
                            : 'text-text-muted'
                        }`}
                      >
                        {lang.code}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="flex items-center">
                <a href="tel:+34952764547" className="flex items-center gap-2 hover:text-gold transition-colors">
                  <Phone size={12} />
                  <span>+34 952 76 45 47</span>
                </a>
                <span className="mx-4 text-gold/30">|</span>
                <Link to="/contacto" className="flex items-center gap-2 hover:text-gold transition-colors">
                  <MapPin size={12} />
                  <span>{t('common.marbella')}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={goToHome}>
              <div className="flex flex-col">
                <span className="font-serif text-2xl lg:text-3xl text-charcoal tracking-wide">
                  Dr. Ángel Norro
                </span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-text-muted">
                  {t('hero.eyebrow').split('·')[0].trim()} · Marbella
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {menuItems.map((menu, index) => (
                <div 
                  key={index}
                  className="relative dropdown"
                  onMouseEnter={() => setActiveDropdown(index)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="flex items-center gap-1 nav-link py-4">
                    {menu.label}
                    <ChevronDown size={14} className={`transition-transform duration-300 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown */}
                  <div className={`dropdown-menu absolute top-full left-0 pt-2 ${activeDropdown === index ? 'opacity-100 visible translate-y-0' : ''}`}>
                    <div className="bg-ivory border border-divider min-w-[280px] py-2">
                      {menu.items.map((item, itemIndex) => {
                        if (item.divider) {
                          return <hr key={itemIndex} className="my-2 border-divider" />;
                        }
                        if (item.heading) {
                          return (
                            <div key={itemIndex} className="px-6 py-2 text-[10px] tracking-[0.2em] uppercase text-gold font-medium">
                              {item.label}
                            </div>
                          );
                        }
                        return (
                          <Link
                            key={itemIndex}
                            to={item.href}
                            className="block px-6 py-3 text-sm text-text-secondary hover:text-gold hover:bg-cream transition-all duration-300"
                          >
                            <span>{item.label}</span>
                            {item.subtitle && (
                              <span className="block text-xs text-text-muted mt-0.5">{item.subtitle}</span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link 
                to="/contacto"
                className="btn-gold inline-block"
              >
                {t('common.requestConsultation')}
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden text-charcoal p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-40 bg-ivory transition-transform duration-500 lg:hidden ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="pt-24 px-6 pb-8 h-full overflow-y-auto">
          {/* Mobile Language Selector */}
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-divider">
            <Globe size={16} className="text-gold" />
            <div className="flex items-center gap-3">
              {Object.entries(languages).map(([code, lang], index) => (
                <React.Fragment key={code}>
                  {index > 0 && <span className="text-divider">|</span>}
                  <button
                    onClick={() => changeLanguage(code)}
                    className={`text-sm transition-colors ${
                      currentLang === code 
                        ? 'text-gold font-medium' 
                        : 'text-text-muted hover:text-gold'
                    }`}
                  >
                    {lang.code}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>

          <nav className="space-y-6">
            {menuItems.map((menu, index) => (
              <div key={index} className="border-b border-divider pb-6">
                <p className="eyebrow mb-4">{menu.label}</p>
                <div className="space-y-3 pl-4">
                  {menu.items.map((item, itemIndex) => {
                    if (item.divider) {
                      return <hr key={itemIndex} className="my-2 border-divider" />;
                    }
                    if (item.heading) {
                      return (
                        <div key={itemIndex} className="text-[10px] tracking-[0.2em] uppercase text-gold font-medium pt-1">
                          {item.label}
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={itemIndex}
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block text-text-secondary hover:text-gold transition-colors"
                      >
                        {item.label}
                        {item.subtitle && (
                          <span className="block text-xs text-text-muted">{item.subtitle}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Mobile contact */}
          <div className="mt-8 space-y-4">
            <a 
              href="tel:+34952764547" 
              className="flex items-center gap-3 text-charcoal"
            >
              <Phone size={18} className="text-gold" />
              <span>+34 952 76 45 47</span>
            </a>
            <Link 
              to="/contacto"
              onClick={() => setIsMobileMenuOpen(false)}
              className="btn-gold block text-center w-full"
            >
              {t('common.requestConsultation')}
            </Link>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-20 lg:h-[136px]"></div>
    </>
  );
};

export default Header;
